import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Mail, Phone, MapPin, Github, Linkedin, ExternalLink, Copy, Check } from 'lucide-react';
// Props: personalInfo

const Contact = ({ personalInfo }) => {
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  if (!personalInfo) return null;
  const handleEmailClick = () => {
  const subject = encodeURIComponent("Portfolio Inquiry");
  const body = encodeURIComponent(
    "Hi Harsha,\n\nI saw your portfolio and would like to connect."
  );

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isMobile) {
    window.location.href = `mailto:${personalInfo.email}?subject=${subject}&body=${body}`;
  } else {
    window.open(
      `https://mail.google.com/mail/?view=cm&fs=1&to=${personalInfo.email}&su=${subject}&body=${body}`,
      "_blank"
    );
  }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'email') {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } else if (type === 'phone') {
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 2000);
    }
  };

  return (
    <section id="contact" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Interested in collaboration, research opportunities, or just want to connect? Feel free to reach out!
          </p>
          <div className="w-20 h-1 bg-blue-900 mx-auto mt-6"></div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Email Card */}
            <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Mail className="w-6 h-6 text-blue-900" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <span
                      onClick={handleEmailClick}
                      className="text-blue-900 hover:underline break-all cursor-pointer"
                    >
                      {personalInfo.email}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => copyToClipboard(personalInfo.email, 'email')}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  {copiedEmail ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Email
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Phone Card */}
            <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Phone className="w-6 h-6 text-blue-900" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a
                      href={`tel:${personalInfo.phone}`}
                      className="text-blue-900 hover:underline"
                    >
                      {personalInfo.phone}
                    </a>
                  </div>
                </div>
                <Button
                  onClick={() => copyToClipboard(personalInfo.phone, 'phone')}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  {copiedPhone ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Phone
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Location Card */}
            <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <MapPin className="w-6 h-6 text-blue-900" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                    <p className="text-gray-700">{personalInfo.location}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* GitHub Card */}
            <Card className="border-2 border-gray-100 hover:border-blue-200 transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Github className="w-6 h-6 text-blue-900" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">GitHub</h3>
                    <a
                      href={personalInfo.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-900 hover:underline break-all flex items-center gap-1"
                    >
                      {personalInfo.github.replace('https://github.com/', '@')}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* LinkedIn */}
          <Card className="border-2 border-blue-100 bg-blue-50/30">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-200 rounded-lg">
                    <Linkedin className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Connect on LinkedIn</h3>
                    <p className="text-sm text-gray-600">Let's connect and stay in touch professionally</p>
                  </div>
                </div>
                <Button
                  onClick={() => window.open(`https://${personalInfo.linkedin}`, '_blank')}
                  className="bg-blue-900 hover:bg-blue-800"
                >
                  View Profile
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;
